import { Box, Button, Typography } from '@mui/material';
import Confetti from 'canvas-confetti';
import { AvailableGiftDTO, CouponGiftDTOGiftTypeEnum, CouponsV2AvailableGiftDTO } from 'probonio-shared-ui/api';
import { LoadingButton } from 'probonio-shared-ui/component/button';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BasicDialog, DialogState } from '../../../component/dialog/BasicDialog';

interface Props {
  gift: AvailableGiftDTO | CouponsV2AvailableGiftDTO;
  dialogState: DialogState;
  infoMessage: string;
  isLoading?: boolean;
  onConfirm: () => void;
}

const GiftModal: React.FC<Props> = ({ gift, dialogState, infoMessage, isLoading, onConfirm }) => {
  const { t } = useTranslation('benefitModule');

  const confetti = useMemo(() => Confetti.create(undefined, { resize: true }), []);

  useEffect(() => {
    if (dialogState.isOpen) {
      void confetti({
        particleCount: 300,
        spread: 360,
        scalar: 2,
        zIndex: 1301,
        decay: 0.9,
        origin: { x: 0.5, y: 0.15 },
        startVelocity: 20,
        disableForReducedMotion: true,
      });
      setTimeout(() => confetti.reset(), 5500);
    }
  }, [confetti, dialogState.isOpen]);

  const isIncentive = gift?.giftType === CouponGiftDTOGiftTypeEnum.Incentive;

  return (
    <BasicDialog
      dialogState={dialogState}
      title={
        <Typography variant="h1" component="span" data-test-id="gift-modal-title">
          {isIncentive
            ? t('benefit.INCENTIVES.giftsModal.giftOccasionTitle')
            : t([`benefit.GIFTS.giftsModal.giftOccasionTitle.${gift.occasion}`, 'benefit.GIFTS.giftsModal.giftOccasionTitle.DEFAULT'])}
        </Typography>
      }
      hideCloseIcon
      maxWidth="xs"
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      content={
        <Box>
          <Typography
            sx={{
              whiteSpace: 'pre-line',
              marginBottom: 1,
              textAlign: 'center',
            }}
          >
            {gift.description}
          </Typography>
          <Typography
            sx={{
              fontStyle: 'italic',
              marginBottom: 1,
              textAlign: 'center',
            }}
          >
            {infoMessage}
          </Typography>
        </Box>
      }
      actions={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <LoadingButton
            loading={isLoading}
            variant="contained"
            onClick={onConfirm}
            sx={{ marginBottom: 0.5 }}
            data-test-id="gift-modal-select-voucher"
          >
            {isIncentive ? t('benefit.INCENTIVES.giftsModal.redeem') : t('benefit.GIFTS.giftsModal.redeem')}
          </LoadingButton>
          <Button
            variant="text"
            color="primary"
            onClick={dialogState.handleClose}
            sx={{ marginBottom: 0.5 }}
            data-test-id="gift-modal-select-later"
            disabled={isLoading}
          >
            {isIncentive ? t('benefit.INCENTIVES.giftsModal.cancelRedeem') : t('benefit.GIFTS.giftsModal.cancelRedeem')}
          </Button>
        </Box>
      }
    />
  );
};

export default GiftModal;
